import { Link, useLocation } from "wouter";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search as SearchIcon, ScanLine, Menu, X, LogOut, User as UserIcon, Shield, Bookmark, Settings } from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/hooks/use-auth";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { useFocusTrap } from "@/hooks/use-focus-trap";
import { resendEmailVerification } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { getCsrf, csrfInit } from "@/lib/auth-client";

export function Header({ onScanClick }: { onScanClick?: () => void }) {
  const [, navigate] = useLocation();
  const [q, setQ] = useState("");
  const [searchError, setSearchError] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const t = useT();
  const { user } = useAuth();

  useScrollLock(open);

  const handleScan = onScanClick ?? (() => navigate("/scan"));

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) {
      setSearchError(true);
      return;
    }
    setSearchError(false);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  function handleQChange(value: string) {
    setQ(value);
    if (searchError && value.trim()) setSearchError(false);
  }

  function handleSearchSubmit(query: string): boolean {
    const trimmed = query.trim();
    if (!trimmed) return false;
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    return true;
  }

  return (
    <header
      className="sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 md:px-8">
        <Link href="/" className="flex flex-col shrink-0">
          <span className="font-serif text-2xl font-black tracking-tight text-foreground leading-none">
            {t("brand.prefix")}<span className="text-[hsl(var(--primary))]">{t("brand.suffix")}</span>
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground leading-none mt-0.5">
            {t("header.tagline")}
          </span>
        </Link>

        <div className="hidden md:flex flex-1 max-w-xl mx-4 flex-col">
          <form onSubmit={submit} className="flex w-full">
            <div
              className={`flex w-full items-center border bg-card focus-within:ring-2 ${
                searchError
                  ? "border-destructive ring-destructive focus-within:ring-destructive"
                  : "border-foreground focus-within:ring-foreground"
              }`}
            >
              <SearchIcon className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
              <input
                value={q}
                onChange={(e) => handleQChange(e.target.value)}
                placeholder={t("header.searchPlaceholder")}
                type="search"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                aria-invalid={searchError}
                className="flex-1 bg-transparent px-3 py-2 text-base placeholder:text-muted-foreground focus:outline-none"
              />
              <button
                type="submit"
                className="bg-foreground text-background px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:bg-[hsl(var(--primary))] transition-colors"
              >
                {t("common.search")}
              </button>
            </div>
          </form>
          {searchError && (
            <p role="alert" className="mt-1 text-xs font-medium text-destructive">
              {t("header.searchRequired")}
            </p>
          )}
        </div>

        <nav className="hidden md:flex items-center gap-6 text-sm">
          <Link href="/blog" className="text-foreground hover:text-[hsl(var(--primary))] transition-colors">
            {t("blog.nav")}
          </Link>
          {user?.isStaff && (
            <Link href="/browse" className="text-foreground hover:text-[hsl(var(--primary))] transition-colors">
              {t("common.browse")}
            </Link>
          )}
          {user?.isStaff && (
            <button
              onClick={handleScan}
              className="inline-flex items-center gap-1.5 text-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              <ScanLine className="h-4 w-4" />
              {t("header.scan")}
            </button>
          )}
          <UserMenu />
        </nav>

        {/* Mobile: search icon + hamburger */}
        <div className="md:hidden ml-auto flex items-center gap-1">
          <button
            onClick={() => setSearchOpen(true)}
            aria-label={t("common.search")}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-foreground hover:text-[hsl(var(--primary))] transition-colors"
          >
            <SearchIcon className="h-5 w-5" />
          </button>
          <button
            aria-label={open ? t("common.close") : t("common.menu")}
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center border border-foreground hover:bg-foreground hover:text-background transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <UnverifiedBanner />

      {open && (
        <MobileDrawer
          id="mobile-nav"
          onScanClick={handleScan}
          onClose={() => setOpen(false)}
          q={q}
          setQ={handleQChange}
          submit={submit}
          searchError={searchError}
        />
      )}

      {searchOpen && (
        <MobileSearchSheet
          onClose={() => setSearchOpen(false)}
          onSubmit={handleSearchSubmit}
        />
      )}
    </header>
  );
}

function UnverifiedBanner() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  if (!user || user.emailVerified) return null;

  async function handleResend() {
    if (busy) return;
    setBusy(true);
    try {
      const token = await getCsrf();
      await resendEmailVerification(csrfInit(token));
      toast({ title: "Verification email sent — check your inbox" });
    } catch {
      toast({ title: "Could not send email", description: "Please try again in a moment." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-[hsl(var(--primary))] text-white text-xs font-mono tracking-[0.15em] px-4 py-2 flex items-center justify-center gap-3">
      <span>Please verify your email to unlock all Kitdex features.</span>
      <button
        onClick={handleResend}
        disabled={busy}
        className="underline hover:no-underline disabled:opacity-60"
      >
        {busy ? "Sending…" : "Resend email"}
      </button>
    </div>
  );
}

function UserMenu() {
  const { user, loading, signOut } = useAuth();
  const t = useT();
  const [openMenu, setOpenMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpenMenu(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (loading) {
    return <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">…</span>;
  }

  if (!user) {
    return (
      <Link
        href="/sign-in"
        className="bg-foreground text-background px-4 py-2 text-xs font-semibold uppercase tracking-widest hover:bg-[hsl(var(--primary))] transition-colors"
      >
        {t("common.signIn")}
      </Link>
    );
  }

  const initial = (user.handle?.[0] ?? user.email[0] ?? "?").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpenMenu((v) => !v)}
        className="inline-flex items-center gap-2 border border-foreground px-2 py-1 hover:bg-foreground hover:text-background transition-colors"
        aria-haspopup="menu"
        aria-expanded={openMenu}
      >
        <Avatar url={user.avatarUrl ?? null} initial={initial} />
        <span className="font-mono text-xs uppercase tracking-[0.15em]">{user.handle}</span>
      </button>
      {openMenu && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 border-2 border-foreground bg-card shadow-[6px_6px_0_0_hsl(var(--foreground))] py-2"
        >
          <div className="px-3 py-2 border-b border-border">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{t("header.signedInAs")}</div>
            <div className="text-sm truncate">{user.email}</div>
          </div>
          {user.isStaff && (
            <Link
              href="/admin"
              onClick={() => setOpenMenu(false)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-foreground hover:text-background flex items-center gap-2 border-b border-border"
            >
              <Shield className="h-3.5 w-3.5" /> Admin
            </Link>
          )}
          <Link
            href="/watchlist"
            onClick={() => setOpenMenu(false)}
            className="w-full text-left px-3 py-2 text-sm hover:bg-foreground hover:text-background flex items-center gap-2 border-b border-border"
          >
            <Bookmark className="h-3.5 w-3.5" /> Watchlist
          </Link>
          <Link
            href="/account/settings"
            onClick={() => setOpenMenu(false)}
            className="w-full text-left px-3 py-2 text-sm hover:bg-foreground hover:text-background flex items-center gap-2 border-b border-border"
          >
            <Settings className="h-3.5 w-3.5" /> Account Settings
          </Link>
          <button
            onClick={() => { setOpenMenu(false); void signOut(); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-foreground hover:text-background flex items-center gap-2"
          >
            <LogOut className="h-3.5 w-3.5" /> {t("header.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}

function Avatar({ url, initial }: { url: string | null; initial: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="h-6 w-6 rounded-full border border-foreground object-cover"
      />
    );
  }
  return (
    <span className="h-6 w-6 inline-flex items-center justify-center rounded-full bg-[hsl(var(--primary))] text-background font-mono text-[11px]">
      {initial}
    </span>
  );
}

/** Full-width search sheet that slides down from the header on mobile */
function MobileSearchSheet({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (query: string) => boolean;
}) {
  const [q, setQ] = useState("");
  const [error, setError] = useState(false);
  const t = useT();
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  useScrollLock(true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function handleChange(value: string) {
    setQ(value);
    if (error && value.trim()) setError(false);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const ok = onSubmit(q);
    if (!ok) setError(true);
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* Backdrop — covers everything below the sheet */}
      <div
        className="fixed inset-0 z-[60] bg-black/50"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Sheet — overlays the header row */}
      <div
        ref={trapRef}
        role="dialog"
        aria-label={t("common.search")}
        aria-modal="true"
        className="fixed top-0 inset-x-0 z-[70] bg-background border-b-2 border-foreground"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="px-4 py-3">
          <form onSubmit={submit} className="flex items-center gap-2">
            <div
              className={`flex flex-1 items-center border bg-card min-w-0 ${
                error ? "border-destructive" : "border-transparent"
              }`}
            >
              <SearchIcon className="h-5 w-5 text-muted-foreground shrink-0" />
              <input
                autoFocus
                value={q}
                onChange={(e) => handleChange(e.target.value)}
                placeholder={t("header.searchPlaceholder")}
                type="search"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                aria-invalid={error}
                className="flex-1 bg-transparent text-base placeholder:text-muted-foreground focus:outline-none py-1 min-w-0"
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close")}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </form>
          {error && (
            <p role="alert" className="mt-1 text-xs font-medium text-destructive">
              {t("header.searchRequired")}
            </p>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Full-height right-anchored navigation drawer */
function MobileDrawer({
  id,
  onScanClick,
  onClose,
  q,
  setQ,
  submit,
  searchError,
}: {
  id?: string;
  onScanClick?: () => void;
  onClose: () => void;
  q: string;
  setQ: (v: string) => void;
  submit: (e: React.FormEvent) => void;
  searchError: boolean;
}) {
  const { user, signOut, loading } = useAuth();
  const t = useT();
  const trapRef = useFocusTrap<HTMLDivElement>(true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/50"
        aria-hidden="true"
        onClick={onClose}
      />
      {/* Drawer panel */}
      <div
        id={id}
        ref={trapRef}
        role="dialog"
        aria-label={t("common.menu")}
        aria-modal="true"
        className="fixed top-0 right-0 z-[70] h-full w-72 max-w-[85vw] bg-background border-l-2 border-foreground flex flex-col overflow-y-auto"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <Link href="/" onClick={onClose} className="flex flex-col">
            <span className="font-serif text-xl font-black leading-none">
              {t("brand.prefix")}<span className="text-[hsl(var(--primary))]">{t("brand.suffix")}</span>
            </span>
          </Link>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search form inside drawer */}
        <form
          onSubmit={(e) => {
            const trimmed = q.trim();
            submit(e);
            if (trimmed) onClose();
          }}
          className="px-4 py-3 border-b border-border shrink-0"
        >
          <div
            className={`flex items-center border bg-card focus-within:ring-2 ${
              searchError
                ? "border-destructive focus-within:ring-destructive"
                : "border-foreground focus-within:ring-foreground"
            }`}
          >
            <SearchIcon className="ml-3 h-4 w-4 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); }}
              placeholder={t("header.searchPlaceholder")}
              aria-invalid={searchError}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              className="flex-1 bg-transparent px-3 py-3 text-base placeholder:text-muted-foreground focus:outline-none"
            />
            <button
              type="submit"
              className="bg-foreground text-background px-3 py-3 font-mono text-[10px] uppercase tracking-widest hover:bg-[hsl(var(--primary))] transition-colors"
            >
              Go
            </button>
          </div>
          {searchError && (
            <p role="alert" className="mt-1 text-xs font-medium text-destructive">
              {t("header.searchRequired")}
            </p>
          )}
        </form>

        {/* Navigation links */}
        <nav className="flex flex-col flex-1 px-4 py-2">
          <Link
            href="/blog"
            onClick={onClose}
            className="min-h-[44px] flex items-center border-b border-border font-medium text-foreground hover:text-[hsl(var(--primary))] transition-colors"
          >
            {t("blog.nav")}
          </Link>
          {user?.isStaff && (
            <Link
              href="/browse"
              onClick={onClose}
              className="min-h-[44px] flex items-center border-b border-border font-medium text-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              {t("common.browse")}
            </Link>
          )}
          {user?.isStaff && (
            <button
              onClick={() => { onClose(); onScanClick?.(); }}
              className="min-h-[44px] flex items-center gap-2 text-left border-b border-border font-medium text-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              <ScanLine className="h-4 w-4" />
              {t("header.scanShirt")}
            </button>
          )}
          {loading ? null : user ? (
            <>
              <div className="min-h-[44px] flex items-center gap-2 border-b border-border text-sm">
                <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="font-mono text-xs truncate">{user.handle}</span>
              </div>
              {user.isStaff && (
                <Link
                  href="/admin"
                  onClick={onClose}
                  className="min-h-[44px] flex items-center gap-2 border-b border-border font-medium"
                >
                  <Shield className="h-4 w-4 shrink-0" /> Admin
                </Link>
              )}
              <Link
                href="/watchlist"
                onClick={onClose}
                className="min-h-[44px] flex items-center gap-2 border-b border-border font-medium"
              >
                <Bookmark className="h-4 w-4 shrink-0" /> Watchlist
              </Link>
              <Link
                href="/account/settings"
                onClick={onClose}
                className="min-h-[44px] flex items-center gap-2 border-b border-border font-medium"
              >
                <Settings className="h-4 w-4 shrink-0" /> Account Settings
              </Link>
              <button
                onClick={() => { onClose(); void signOut(); }}
                className="min-h-[44px] flex items-center gap-2 text-left border-b border-border font-medium"
              >
                <LogOut className="h-4 w-4 shrink-0" /> {t("header.signOut")}
              </button>
            </>
          ) : (
            <Link
              href="/sign-in"
              onClick={onClose}
              className="mt-4 bg-foreground text-background text-center min-h-[44px] flex items-center justify-center font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--primary))] transition-colors"
            >
              {t("common.signIn")}
            </Link>
          )}
        </nav>
      </div>
    </>,
    document.body,
  );
}
